import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "bg-[#1E222D] border-[#2A2E39] text-[#EAECEF] placeholder:text-[#5E6673] focus-visible:ring-[#F0B90B]/50 font-mono";
const labelCls = "text-[10px] font-medium uppercase tracking-wider text-[#848E9C]";

export default function EditTradeDialog({ trade, onClose, onDone }) {
  const [form, setForm] = useState(null);
  const [pnlTouched, setPnlTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (trade) {
      setPnlTouched(false);
      setForm({
        symbol: trade.symbol,
        type: trade.type,
        volume: String(trade.volume),
        open_price: String(trade.open_price),
        close_price: trade.close_price != null ? String(trade.close_price) : "",
        sl: trade.sl != null ? String(trade.sl) : "",
        tp: trade.tp != null ? String(trade.tp) : "",
        profit: trade.status === "closed" ? String(trade.profit) : "",
        open_time: trade.open_time ? dayjs(trade.open_time).format("YYYY-MM-DDTHH:mm") : "",
        close_time: trade.close_time ? dayjs(trade.close_time).format("YYYY-MM-DDTHH:mm") : "",
      });
    }
  }, [trade]);

  if (!form) return <Dialog open={!!trade} onOpenChange={(o) => !o && onClose()} />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        symbol: form.symbol,
        type: form.type,
        volume: parseFloat(form.volume),
        open_price: parseFloat(form.open_price),
        close_price: form.close_price ? parseFloat(form.close_price) : null,
        sl: form.sl ? parseFloat(form.sl) : null,
        tp: form.tp ? parseFloat(form.tp) : null,
        open_time: form.open_time || null,
        close_time: form.close_time || null,
      };
      if (trade.status === "closed" && pnlTouched) payload.profit = parseFloat(form.profit);
      await api.patch(`/admin/trades/${trade.id}`, payload);
      toast.success(`Trade #${trade.ticket} updated — client allocations recalculated`);
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!trade} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-trade-modal" className="max-h-[90vh] overflow-y-auto border-[#2A2E39] bg-[#131722] text-[#EAECEF] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">
            Edit Trade <span className="font-mono text-[#F0B90B]">#{trade?.ticket}</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-[#848E9C]">
            {trade?.status === "closed"
              ? "Changing prices/volume auto-recomputes P&L and adjusts client balances. Edit P&L manually only to override."
              : "Changes apply to all allocated client accounts."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className={labelCls}>Symbol</Label>
            <select data-testid="edit-trade-symbol-select" value={form.symbol} onChange={set("symbol")} className="w-full rounded-md border border-[#2A2E39] bg-[#1E222D] px-3 py-2 font-mono text-sm text-[#EAECEF] focus:outline-none focus:ring-1 focus:ring-[#F0B90B]/50">
              <option value="XAUUSD">XAUUSD</option>
              <option value="XAGUSD">XAGUSD</option>
              <option value="GOLD">GOLD</option>
              <option value="BTCUSDT">BTCUSDT</option>
              <option value="ETHUSDT">ETHUSDT</option>
              <option value="SOLUSDT">SOLUSDT</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Type</Label>
            <select data-testid="edit-trade-type-select" value={form.type} onChange={set("type")} className="w-full rounded-md border border-[#2A2E39] bg-[#1E222D] px-3 py-2 font-mono text-sm text-[#EAECEF] focus:outline-none focus:ring-1 focus:ring-[#F0B90B]/50">
              <option value="buy">BUY</option>
              <option value="sell">SELL</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Volume (Lots)</Label>
            <Input data-testid="edit-trade-volume-input" type="number" step="0.0001" min="0.0001" value={form.volume} onChange={set("volume")} className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Open Price</Label>
            <Input data-testid="edit-trade-open-price-input" type="number" step="0.01" value={form.open_price} onChange={set("open_price")} className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Close Price</Label>
            <Input data-testid="edit-trade-close-price-input" type="number" step="0.01" value={form.close_price} onChange={set("close_price")} className={inputCls} />
          </div>
          {trade?.status === "closed" && (
            <div className="space-y-1.5">
              <Label className={labelCls}>P&L Override</Label>
              <Input data-testid="edit-trade-pnl-input" type="number" step="0.01" value={form.profit} onChange={(e) => { setPnlTouched(true); setForm((f) => ({ ...f, profit: e.target.value })); }} className={inputCls} />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className={labelCls}>Stop Loss</Label>
            <Input data-testid="edit-trade-sl-input" type="number" step="0.01" value={form.sl} onChange={set("sl")} className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Take Profit</Label>
            <Input data-testid="edit-trade-tp-input" type="number" step="0.01" value={form.tp} onChange={set("tp")} className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Open Date & Time</Label>
            <Input data-testid="edit-trade-open-time-input" type="datetime-local" value={form.open_time} onChange={set("open_time")} className={`${inputCls} [color-scheme:dark]`} />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Close Date & Time</Label>
            <Input data-testid="edit-trade-close-time-input" type="datetime-local" value={form.close_time} onChange={set("close_time")} className={`${inputCls} [color-scheme:dark]`} />
          </div>
        </div>
        <button
          data-testid="submit-edit-trade-btn"
          onClick={submit}
          disabled={loading}
          className="mt-4 w-full rounded-md bg-[#F0B90B] py-2.5 text-sm font-semibold text-black shadow-[0_0_12px_rgba(240,185,11,0.25)] transition-colors hover:bg-[#F8D12F] disabled:opacity-50"
        >
          {loading ? "Saving..." : "Save Trade Changes"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
